import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { AddressMedia } from "@/api.types.v2";
import { useSession } from "@/services/auth0/session";

interface AddressMediasResponse {
  data: AddressMedia[];
  pagination: {
    totalCount: number;
    currentPage: number;
    totalPages: number;
  };
}

// Fetch all address medias with pagination
export function useAddressMedias(page = 1, limit = 50) {
  const { token } = useSession();
  return useQuery({
    queryKey: ["address-medias", page, limit],
    queryFn: async () => {
      const response = await axios.get<AddressMediasResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/address-medias`,
        {
          params: { page, limit },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    enabled: !!token,
  });
}

// Fetch a single address media by ID
export function useAddressMedia(id: string) {
  const { token } = useSession();
  return useQuery({
    queryKey: ["address-media", id],
    queryFn: async () => {
      const response = await axios.get<AddressMedia>(
        `${import.meta.env.VITE_API_URL}/api/v2/address-media/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    enabled: !!id && !!token,
  });
}

// Delete an address media
export function useDeleteAddressMedia() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete<AddressMedia>(
        `${import.meta.env.VITE_API_URL}/api/v2/address-media/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["address-medias"] });
    },
  });
}

// Delete a media item from an address media
export function useDeleteMediaItem() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      addressMediaId,
      mediaItemId,
    }: {
      addressMediaId: string;
      mediaItemId: string;
    }) => {
      const response = await axios.delete<AddressMedia>(
        `${import.meta.env.VITE_API_URL}/api/v2/address-media/${addressMediaId}/media/${mediaItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["address-medias"] });
      void queryClient.invalidateQueries({ queryKey: ["address-media"] });
    },
  });
}

export interface AddressMediaFields {
  placeId?: string;
  streetNumber?: string;
  streetName: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
}

export interface CreateAddressMediaInput extends AddressMediaFields {
  mediaItems: Array<{
    base64Data: string;
    type: "IMAGE";
    mimeType: string;
    description?: string;
  }>;
}

export function useCreateAddressMedia() {
  const { token } = useSession();
  const client = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (input: CreateAddressMediaInput) => {
      const { data } = await axios.post<AddressMedia>(
        `${import.meta.env.VITE_API_URL}/api/v2/address-media`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!data?.id)
        throw new Error("Creation outcome could not be confirmed.");
      return data;
    },
    onSuccess: (data) => {
      client.setQueryData(["address-media", data.id], data);
      void client.invalidateQueries({ queryKey: ["address-medias"] });
    },
  });
}

export function useSearchAddressMedia(fields: AddressMediaFields | null) {
  const { token } = useSession();
  const params = fields
    ? {
        streetName: fields.streetName,
        city: fields.city,
        state: fields.state,
        country: fields.country,
        postalCode: fields.postalCode,
        placeId: fields.placeId,
      }
    : null;
  return useQuery({
    queryKey: ["address-media-search", params],
    enabled: !!token && !!fields,
    retry: false,
    queryFn: async () => {
      const { data } = await axios.get<AddressMedia | null>(
        `${import.meta.env.VITE_API_URL}/api/v2/address-media`,
        { params, headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
  });
}
